import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';

import activityTracker from '@services/activityLogger';
import { refreshAccessToken } from '@services/api';
import UserAuth from '@services/auth';
import { NAV_ROUTES } from '@constants/navRoutes';
import store, { RootState, useAppDispatch } from '@stores/store';
import { getDashboardDataBasedOnUrl, prepareAndNavigateForAuthentication } from '@utils/common';

const DASHBOARD_ROUTE_PATTERNS = [
  NAV_ROUTES.DASHBOARD_MODULE_SUBMODULE_ID,
  NAV_ROUTES.DASHBOARD_MODULE_SUBMODULE,
  NAV_ROUTES.DASHBOARD_MODULE,
];

// The user's role in a module, recorded on user actions: FULLACCESS when the submodule has full
// access (the case usePermission treats as admin), otherwise the user's role (ADMIN or VIEWER).
// Read from the store when the action happens.
const getRoleInSubmodule = (submoduleId: string | null): string | null => {
  const { UserData, ModulesData } = store.getState();
  const submodule = submoduleId
    ? ModulesData.modules
        ?.flatMap((module) => module.submodules || [])
        .find((item) => item.id === submoduleId)
    : undefined;

  if (submodule?.hasFullAccess) return 'FULLACCESS';
  return UserData.user?.role || null;
};

// Starts activity tracking for the logged in user and reports each view mounted on a route change
const useActivityTracker = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const userId = useSelector((state: RootState) => state?.UserData?.user?.id);
  const appToken = useSelector((state: RootState) => state?.AppData?.auth?.access_token);
  const modules = useSelector((state: RootState) => state?.ModulesData);

  useEffect(() => {
    if (userId) {
      activityTracker.start(
        userId,
        appToken || UserAuth.accessToken,
        {
          refreshAccessToken: () => refreshAccessToken(dispatch),
          navigateToAuth: prepareAndNavigateForAuthentication,
        },
        getRoleInSubmodule,
      );
    }
  }, [userId]);

  useEffect(() => {
    // '/dashboard' only redirects to the user's first dashboard
    if (!userId || pathname === NAV_ROUTES.DASHBOARD) return;

    let submoduleId: string | null = null;
    const dashboardMatch = DASHBOARD_ROUTE_PATTERNS.map((pattern) =>
      matchPath(pattern, pathname),
    ).find(Boolean);

    if (dashboardMatch) {
      // The submodule id comes from the modules list, so wait until it has loaded
      if (modules.loadCount === 0) return;

      // Same lookup the Dashboard page uses to resolve the submodule
      const submodulePath = dashboardMatch.params.submodule || pathname;
      submoduleId = getDashboardDataBasedOnUrl(modules.modules, submodulePath)?.id || null;
    }

    activityTracker.setView({ route: pathname, submoduleId });
  }, [pathname, userId, modules.loadCount, modules.modules]);
};

export default useActivityTracker;
